import React, { useState } from 'react';
import { Play, Clock, CheckCircle, Edit3, Trash2, FileText, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { getItemThumb, NIVEL_COLORS } from './academiaUtils';

/**
 * Card estilo Netflix: a THUMBNAIL 16:9 é o card. Texto e ações aparecem
 * num overlay no hover (desktop); barra de progresso fina na base; badges
 * sobrepostos na imagem. Sem borda/fundo de "card de dashboard".
 */
const MediaCard = ({
  item, progress, onOpen, isNew, t,
  isSuperAdmin, onEdit, onDelete,
  onMoveLeft, onMoveRight,
  ordinal = null,               // nº de ordem (1-based) — chip "01" p/ deixar a sequência clara
  width = 340, isMobile = false,
}) => {
  const [hover, setHover] = useState(false);
  const thumb = getItemThumb(item);
  const nivelColor = item.nivel ? (NIVEL_COLORS[item.nivel] || 'var(--dp-text-tertiary)') : null;
  const isRecurso = item.tipo === 'recurso';
  const FallbackIcon = isRecurso ? FileText : BookOpen;
  const showOverlay = hover && !isMobile;

  return (
    <div
      onClick={() => onOpen(item)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        // width=0 → o pai (grid) controla a largura; senão card de trilho
        width: width === 0 ? '100%' : (isMobile ? '72vw' : `${width}px`),
        maxWidth: isMobile && width !== 0 ? '300px' : 'none',
        flexShrink: 0,
        aspectRatio: '16 / 9',
        position: 'relative',
        borderRadius: '10px',
        overflow: 'hidden',
        cursor: 'pointer',
        background: 'linear-gradient(135deg, var(--dp-bg-card), var(--dp-bg-elevated))',
        transform: showOverlay ? 'scale(1.045)' : 'scale(1)',
        zIndex: showOverlay ? 3 : 1,
        boxShadow: showOverlay ? '0 12px 32px rgba(0,0,0,0.55)' : 'none',
        transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1), box-shadow 0.25s ease',
      }}
    >
      {/* Imagem protagonista */}
      {thumb ? (
        <img
          src={thumb}
          alt={item.titulo}
          loading="lazy"
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', display: 'block',
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        /* Fallback sem capa: gradiente do sistema + ícone + título */
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px',
        }}>
          <FallbackIcon size={26} style={{ color: 'rgba(13,174,255,0.55)' }} />
          <span style={{
            fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-secondary)',
            fontFamily: 'var(--dp-font)', textAlign: 'center', lineHeight: 1.35,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {item.titulo}
          </span>
        </div>
      )}

      {/* Gradiente de leitura permanente na base (título sutil quando tem capa) */}
      {thumb && !showOverlay && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: '52%',
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.78))',
          display: 'flex', alignItems: 'flex-end', padding: '10px 12px',
        }}>
          <span style={{
            fontSize: '12.5px', fontWeight: 600, color: '#F5F5F5',
            fontFamily: 'var(--dp-font)', lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.titulo}
          </span>
        </div>
      )}

      {/* Overlay de hover: título + meta + play (linguagem Netflix) */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.86) 100%)',
        opacity: showOverlay ? 1 : 0,
        pointerEvents: showOverlay ? 'auto' : 'none',
        transition: 'opacity 0.2s ease',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        padding: '12px 14px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: '0 0 4px 0', fontSize: '13px', fontWeight: 700, color: '#F5F5F5',
              fontFamily: 'var(--dp-font)', lineHeight: 1.3,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {item.titulo}
            </p>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
              fontSize: '10.5px', color: '#A0A0A0', fontFamily: 'var(--dp-font)',
            }}>
              {item.total_aulas > 0 && (
                <span>{item.total_aulas} {t('academia.labels.aulas')}</span>
              )}
              {item.duracao_estimada && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <Clock size={10} /> {item.duracao_estimada}
                </span>
              )}
              {item.nivel && (
                <span style={{ color: nivelColor, fontWeight: 600 }}>
                  {t(`academia.niveis.${item.nivel}`)}
                </span>
              )}
            </div>
          </div>
          <div style={{
            width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Play size={15} color="#fff" fill="#fff" style={{ marginLeft: '2px' }} />
          </div>
        </div>

        {/* Ações admin dentro do overlay */}
        {isSuperAdmin && !item.__mock && (
          <div style={{
            position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '4px',
          }}>
            {onMoveLeft && (
              <button
                onClick={(e) => { e.stopPropagation(); onMoveLeft(item); }}
                title={t('academia.home.move_left') || 'Mover para a esquerda'}
                style={{
                  background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '6px',
                  padding: '5px', cursor: 'pointer', display: 'flex',
                }}
              >
                <ChevronLeft size={12} style={{ color: '#F5F5F5' }} />
              </button>
            )}
            {onMoveRight && (
              <button
                onClick={(e) => { e.stopPropagation(); onMoveRight(item); }}
                title={t('academia.home.move_right') || 'Mover para a direita'}
                style={{
                  background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '6px',
                  padding: '5px', cursor: 'pointer', display: 'flex',
                }}
              >
                <ChevronRight size={12} style={{ color: '#F5F5F5' }} />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onEdit?.(item, e); }}
              style={{
                background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '6px',
                padding: '5px', cursor: 'pointer', display: 'flex',
              }}
            >
              <Edit3 size={12} style={{ color: '#F5F5F5' }} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete?.(item, e); }}
              style={{
                background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '6px',
                padding: '5px', cursor: 'pointer', display: 'flex',
              }}
            >
              <Trash2 size={12} style={{ color: '#F87171' }} />
            </button>
          </div>
        )}
      </div>

      {/* Chip de ORDEM (01, 02...) — sequência clara na fileira. Fica no
          canto superior esquerdo; quando presente, tem prioridade sobre o
          badge "Novo" (evita empilhar no mesmo canto). */}
      {ordinal != null && (
        <span style={{
          position: 'absolute', top: '8px', left: '8px', zIndex: 2,
          minWidth: '22px', height: '22px', padding: '0 6px', borderRadius: '7px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', fontWeight: 800, fontFamily: 'var(--dp-font)',
          background: 'rgba(0,0,0,0.68)', color: '#F5F5F5',
          border: '1px solid rgba(255,255,255,0.18)', letterSpacing: '0.3px',
          backdropFilter: 'blur(2px)',
        }}>
          {String(ordinal).padStart(2, '0')}
        </span>
      )}

      {/* Badges sobrepostos (sempre na thumbnail) */}
      {!showOverlay && (
        <>
          {item.__mock && (
            <span style={{
              position: 'absolute', top: '8px', right: '8px',
              padding: '2px 8px', borderRadius: '4px',
              fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px',
              background: 'rgba(0,0,0,0.65)', color: '#FBBF24',
              border: '1px solid rgba(251,191,36,0.35)',
              textTransform: 'uppercase', fontFamily: 'var(--dp-font)',
            }}>
              {t('academia.home.mock_badge') || 'Exemplo'}
            </span>
          )}
          {isNew && ordinal == null && (
            <span style={{
              position: 'absolute', top: '8px', left: '8px',
              padding: '2px 8px', borderRadius: '4px',
              fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px',
              background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', color: '#fff',
              textTransform: 'uppercase', fontFamily: 'var(--dp-font)',
            }}>
              {t('academia.home.new_badge') || 'Novo'}
            </span>
          )}
          {item.user_watched && (
            <span style={{
              position: 'absolute', top: '8px', right: '8px',
              width: '20px', height: '20px', borderRadius: '50%',
              background: 'rgba(16,185,129,0.9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CheckCircle size={12} color="#fff" />
            </span>
          )}
        </>
      )}

      {/* Barra de progresso fina na base */}
      {progress != null && progress > 0 && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: '3px',
          background: 'rgba(255,255,255,0.18)',
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(100, progress)}%`,
            background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
          }} />
        </div>
      )}
    </div>
  );
};

export default MediaCard;
